import { memo, useCallback, useEffect, useMemo } from 'react';
import { Button, Card, Checkbox, RadioButton, TextArea } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import { INVITE_MODE } from '../../../constants/inviteModes.js';
import CatalogChecklist from '../review/CatalogChecklist.jsx';
import InviteNoAccessNotice from './InviteNoAccessNotice.jsx';

/**
 * Invitations of step 4. Nobody gets an email unless chosen; the default is Don't invite yet.
 *
 * @param {object} props
 * @param {{ mode: string, selectedIds: number[], reinviteExisting: boolean, note: string }} props.invite
 * @param {{ all: number, withAccess: number, selected: number, existingUnjoined: number }} props.counts
 * @param {object[]} props.noAccessPeople New rows that will be invited with no access (a warning lists them).
 * @param {number} props.inviteTotal Invitations the chosen option sends: the note shows when it is more than 0.
 * @param {string[]} props.unjoinedNames Names of the existing members who have not joined yet.
 * @param {object[]} props.people New rows (options of "Choose who to invite").
 * @param {(change: object) => void} props.onChange
 */
function InvitationOptions({ invite, counts, unjoinedNames, inviteTotal, noAccessPeople, people, onChange }) {
  const { t } = useTranslation('people-access');
  const items = useMemo(
    () =>
      people.map((row) => ({
        id: row.rowNumber,
        name: `${row.person.first_name} ${row.person.last_name}`.trim() || row.person.email,
        subtitle: row.access ? row.person.email : `${row.person.email} · ${t('import.check.before.noAccessYet')}`,
      })),
    [people, t],
  );
  const handleSelectAll = useCallback(
    () => onChange({ selectedIds: people.map((row) => row.rowNumber) }),
    [onChange, people],
  );
  const handleToggle = useCallback(
    (id) =>
      onChange({
        selectedIds: invite.selectedIds.includes(id)
          ? invite.selectedIds.filter((x) => x !== id)
          : [...invite.selectedIds, id],
      }),
    [onChange, invite.selectedIds],
  );

  // "Only people with access" disappears when nobody has access any more: do not keep it chosen.
  useEffect(() => {
    if (invite.mode === INVITE_MODE.WITH_ACCESS && counts.withAccess === 0) onChange({ mode: INVITE_MODE.NONE });
  }, [invite.mode, counts.withAccess, onChange]);

  const options = [
    { mode: INVITE_MODE.NONE, label: t('import.check.invitations.none.label'), hint: t('import.check.invitations.none.hint') },
    { mode: INVITE_MODE.ALL, label: t('import.check.invitations.all.label', { count: counts.all }), hint: t('import.check.invitations.all.hint') },
    {
      mode: INVITE_MODE.WITH_ACCESS,
      label: t('import.check.invitations.withAccess.label', { count: counts.withAccess }),
      hint: counts.withAccess === 0 ? t('import.check.invitations.withAccess.hintNone') : t('import.check.invitations.withAccess.hint'),
      disabled: counts.withAccess === 0,
    },
    { mode: INVITE_MODE.SELECTED, label: t('import.check.invitations.selected.label'), hint: t('import.check.invitations.selected.hint') },
  ];

  return (
    <Card className="flex flex-col gap-4 p-5">
      <div>
        <h2 className="text-sm font-bold uppercase tracking-wide text-content">{t('import.check.invitations.title')}</h2>
        <p className="text-sm text-content-secondary">{t('import.check.invitations.subtitle')}</p>
      </div>
      <div role="radiogroup" aria-label={t('import.check.invitations.title')} className="flex flex-col gap-3">
        {options.map(({ mode, label, hint, disabled }) => (
          <label key={mode} className={`flex items-start gap-3 ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
            <RadioButton
              checked={invite.mode === mode}
              disabled={disabled}
              aria-label={label}
              onChange={() => onChange({ mode })}
            />
            <span className="flex flex-col">
              <span className="text-sm text-content">{label}</span>
              <span className="text-xs text-content-secondary">{hint}</span>
            </span>
          </label>
        ))}
      </div>
      {invite.mode === INVITE_MODE.SELECTED ? (
        <div className="flex flex-col gap-1">
          <CatalogChecklist
            items={items}
            selectedIds={invite.selectedIds}
            onToggle={handleToggle}
            searchLabel={t('import.check.invitations.selected.search')}
            summary={
              <div className="flex items-center justify-between text-xs text-content-secondary">
                <span>{t('import.check.invitations.selected.progress', { count: counts.selected, total: people.length })}</span>
                <Button variant="ghost" size="sm" onClick={handleSelectAll}>
                  {t('import.check.invitations.selected.selectAll')}
                </Button>
              </div>
            }
          />
        </div>
      ) : null}
      <InviteNoAccessNotice people={noAccessPeople} />
      {counts.existingUnjoined > 0 ? (
        <div className="flex items-start gap-3">
          <Checkbox
            checked={invite.reinviteExisting}
            aria-label={t('import.check.invitations.reinvite', { count: counts.existingUnjoined })}
            onChange={() => onChange({ reinviteExisting: !invite.reinviteExisting })}
          />
          <div className="flex flex-col">
            <span className="text-sm text-content">
              {t('import.check.invitations.reinvite', { count: counts.existingUnjoined })}
            </span>
            <span className="text-xs text-content-secondary">{t('import.check.invitations.reinviteHint', { names: unjoinedNames.join(', ') })}</span>
          </div>
        </div>
      ) : null}
      {inviteTotal > 0 ? (
        <div className="flex flex-col gap-1.5">
          <label htmlFor="import-invite-note" className="sr-only">
            {t('import.check.invitations.note')}
          </label>
          <TextArea
            id="import-invite-note"
            rows={3}
            value={invite.note}
            placeholder={t('import.check.invitations.notePlaceholder')}
            onChange={(event) => onChange({ note: event.target.value })}
          />
        </div>
      ) : null}
    </Card>
  );
}

export default memo(InvitationOptions);
